/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { Sale, SalePaperworkMilestoneType } from '../types/sale.types';

/**
 * What "needs you today" means for a sale.
 *
 * Unlike the lease board, every figure the sale needs travels on the record
 * itself: `paperwork` carries the dates, `commission_receipts` the money
 * that arrived, and `accepted_value` × `commission_percentage` what was
 * expected. `useSales` loads the whole registry, so the counts above the
 * board are true totals rather than a page's worth.
 *
 * The one thing that does *not* travel on the record is the backend's
 * `commission_panel`, which the detail screen uses precisely so nothing is
 * computed twice. Here the panel is not available per row, so the
 * outstanding amount is recomputed — deliberately by the same rule the
 * backend uses, and deliberately only for a *decision* (does this sale owe
 * money) rather than for a figure shown as an authority. The detail screen
 * remains the only place that prints the amount, and it prints the
 * backend's.
 */

/**
 * Which milestones a sale still owes.
 *
 * Counted over the required types only, the same set the backend pends.
 * An optional milestone (financing, on a cash purchase) has nothing to
 * approve, so leaving it blank is not a pendency.
 *
 * @param sale - The sale to inspect.
 * @param types - The agency's configured milestone types.
 * @returns The pending types, in the agency's configured order.
 */
export function pendingMilestones(
  sale: Sale,
  types: readonly SalePaperworkMilestoneType[],
): SalePaperworkMilestoneType[] {
  return [...types]
    .sort((a, b) => a.order - b.order)
    .filter((type) => type.required && !sale.paperwork?.[type.id]);
}

/**
 * What the commission still owes, in the record's own currency unit.
 *
 * @param sale - The sale to total.
 * @returns Expected, received and the difference, never below zero.
 */
export function commissionTotals(sale: Sale): {
  expected: number; received: number; outstanding: number;
} {
  const expected = (sale.accepted_value ?? 0) * (sale.commission_percentage ?? 0) / 100;
  const received = (sale.commission_receipts ?? []).reduce(
    (sum, receipt) => sum + (receipt.amount ?? 0), 0,
  );
  return { expected, received, outstanding: Math.max(0, expected - received) };
}

export type SaleTriageId = 'paperwork' | 'commission' | 'registry';

/** The groups, in reading order. */
export const SALE_TRIAGE_IDS: readonly SaleTriageId[] = [
  'paperwork', 'commission', 'registry',
];

/** The accent token each group is painted in. */
export const SALE_TRIAGE_TONES: Record<SaleTriageId, string> = {
  paperwork: 'var(--accent-mostarda)',
  commission: 'var(--accent-petroleo)',
  registry: 'var(--accent-cobre)',
};

/**
 * Whether a sale belongs to a triage group.
 *
 * @param sale - The sale to test.
 * @param id - The group.
 * @param types - The agency's configured milestone types.
 * @returns Whether it qualifies.
 */
export function matchesSaleTriage(
  sale: Sale,
  id: SaleTriageId,
  types: readonly SalePaperworkMilestoneType[],
): boolean {
  if (id === 'paperwork') return hasPendingMilestones(sale, types);
  if (id === 'commission') return hasOutstandingCommission(sale);
  return sale.state === 'in_paperwork' && !sale.paperwork?.registry;
}

/**
 * In paperwork with at least one milestone still open.
 */
function hasPendingMilestones(
  sale: Sale,
  types: readonly SalePaperworkMilestoneType[],
): boolean {
  return sale.state === 'in_paperwork' && pendingMilestones(sale, types).length > 0;
}

/**
 * A voided sale never happened, so there is nothing to reconcile against it —
 * `canRecordCommission` refuses receipts there for the same reason, and
 * counting it would be chasing money for a deal that was called off.
 */
function hasOutstandingCommission(sale: Sale): boolean {
  return sale.state !== 'voided' && commissionTotals(sale).outstanding > 0;
}

/**
 * Counts per group, over every sale the page holds.
 *
 * @param sales - The whole registry, as `useSales` returns it.
 * @param types - The agency's configured milestone types.
 * @returns One count per group id.
 */
export function countSaleTriage(
  sales: readonly Sale[],
  types: readonly SalePaperworkMilestoneType[],
): Record<SaleTriageId, number> {
  return SALE_TRIAGE_IDS.reduce((counts, id) => ({
    ...counts,
    [id]: sales.filter((sale) => matchesSaleTriage(sale, id, types)).length,
  }), {} as Record<SaleTriageId, number>);
}

/**
 * Narrows the registry to one triage group.
 *
 * @param sales - The sales already filtered and searched.
 * @param id - The selected group, or null to leave them alone.
 * @param types - The agency's configured milestone types.
 * @returns The matching sales, or the input when nothing is selected.
 */
export function applySaleTriage<T extends Sale>(
  sales: readonly T[],
  id: SaleTriageId | null,
  types: readonly SalePaperworkMilestoneType[],
): readonly T[] {
  if (!id) return sales;
  return sales.filter((sale) => matchesSaleTriage(sale, id, types));
}
